import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t } from '@/i18n';
import type { RunEndData } from './run-end-screen';
import { BODY_W, BODY_X, buildEarnedList } from './victory';
import { bodyText, hexPath, label } from './widgets';

const PIP_R = 26;
const PIP_Y = 196;

/**
 * Sector-mode game over (roguelike death, docs/01): where the run ended on
 * the room track, the fresh-build note and any achievements earned.
 */
export function buildSectorLostBody(data: RunEndData): Container {
  const c = new Container();
  const where = label(
    t('sectorLost.where', { n: data.sector + 1, room: data.room + 1, total: data.rooms }),
    20, COLORS.dangerOrange, 2,
  );
  where.position.set(BODY_X, 108);
  c.addChild(where);
  c.addChild(roomTrack(data.room, data.rooms));
  const note = bodyText(t('sectorLost.fresh'), 14, 0xb8bcc6, BODY_W);
  note.anchor.set(0, 0);
  note.style.align = 'left';
  note.position.set(BODY_X, 246);
  c.addChild(note);
  c.addChild(buildEarnedList(data.earned, 300));
  return c;
}

/** Room pips: cleared rooms lit, the fatal one cracked red, the rest dark. */
function roomTrack(failed: number, rooms: number): Container {
  const c = new Container();
  const step = Math.min(96, (BODY_W - PIP_R * 2) / Math.max(1, rooms - 1));
  const g = new Graphics();
  const x0 = BODY_X + PIP_R;
  g.moveTo(x0, PIP_Y).lineTo(x0 + step * (rooms - 1), PIP_Y)
    .stroke({ color: 0x2a2440, width: 3 });
  c.addChild(g);
  for (let i = 0; i < rooms; i++) {
    const x = x0 + i * step;
    const done = i < failed;
    const lost = i === failed;
    const finale = i === rooms - 1;
    const pip = new Graphics();
    hexPath(pip, x, PIP_Y, finale ? PIP_R + 4 : PIP_R)
      .fill({ color: lost ? 0x3a0e12 : done ? 0x16302c : 0x15121f })
      .stroke({ color: lost ? COLORS.dangerRed : done ? COLORS.uiAccent : 0x3a3450, width: 3 });
    if (lost) {
      pip.moveTo(x - 9, PIP_Y - 9).lineTo(x + 9, PIP_Y + 9)
        .moveTo(x + 9, PIP_Y - 9).lineTo(x - 9, PIP_Y + 9)
        .stroke({ color: COLORS.dangerRed, width: 3 });
    }
    c.addChild(pip);
    if (!lost) {
      const n = label(finale ? '★' : String(i + 1), 15, done ? COLORS.uiAccent : 0x6a6480, 0);
      n.anchor.set(0.5);
      n.position.set(x, PIP_Y);
      c.addChild(n);
    }
  }
  return c;
}
